import create from 'zustand'
import {
  createCheckoutSession,
  createPortalSession,
  cancelDowngradeRequest,
  endTrialNow,
  getBillingInvoices,
  getBillingSubscription,
  syncBillingFromStripe,
  upgradeSubscription
} from '../api/billingApi'
import { getFriendlyErrorMessage } from '../utils/errorMessage'
import { useCenterStore } from './CenterStore'
import { useEntitlementStore } from './EntitlementStore'

const getActiveCcid = () => useCenterStore.getState().ccId

const initialState = {
  // SubscriptionView from GET /v1/billing/subscription, or null before the first load.
  subscription: null,
  invoices: [],
  isLoading: false,
  error: null,
  // Which center the current data belongs to, so a center switch is visible as stale.
  loadedForCcId: null,
  portalPending: false,
  portalError: null,
  checkoutPending: false,
  checkoutError: null,
  upgradePending: false,
  upgradeError: null,
  upgradeMessage: null
}

/**
 * Billing tab state (Settings → Billing). Not persisted: billing state must always reflect
 * the backend, which the Stripe webhook pipeline keeps current.
 */
const useBillingStore = create((set, get) => ({
  ...initialState,

  reset: () => set({ ...initialState }),

  /** Clears one-shot notices (upgrade result, action errors). */
  clearNotices: () => set({ upgradeMessage: null, upgradeError: null, checkoutError: null, portalError: null }),

  /** Loads the subscription view for the active center, then invoices (non-fatal) when one exists. */
  loadBilling: async () => {
    const ccId = getActiveCcid()
    if (!ccId) {
      set({ ...initialState })
      return
    }
    set({ isLoading: true, error: null })
    try {
      const subscription = await getBillingSubscription(ccId)
      let invoices = []
      if (subscription?.hasSubscription) {
        try {
          invoices = (await getBillingInvoices(ccId)) || []
        } catch (invoiceError) {
          // Invoice history is secondary; the tab still renders without it.
          invoices = []
        }
      }
      set({ subscription, invoices, isLoading: false, loadedForCcId: ccId })
    } catch (error) {
      set({ isLoading: false, error: getFriendlyErrorMessage(error) })
    }
  },

  /**
   * Reconciles local state straight from Stripe, then reloads the view. Used on return from
   * Checkout so the new subscription shows immediately, independent of webhook delivery.
   */
  syncFromStripe: async () => {
    const ccId = getActiveCcid()
    if (!ccId) return
    set({ isLoading: true, error: null })
    try {
      await syncBillingFromStripe(ccId)
    } catch (syncError) {
      // Sync is best-effort; the regular load still runs and the webhook pipeline remains the backstop.
    }
    await get().loadBilling()
    // A purchase can change what the location may use (e.g. a new sign-up's first package
    // unlocks everything): refresh the nav, routes and status bar now, not on the next page load.
    await useEntitlementStore.getState().loadEntitlements(ccId, { force: true })
  },

  /**
   * Sends the browser to Stripe Checkout for a tier (signup / reactivation / trial conversion). `promoCode`:
   * optional — a new location's first plan, free for the code's days.
   */
  startCheckout: async (tier, promoCode) => {
    const ccId = getActiveCcid()
    set({ checkoutPending: true, checkoutError: null })
    try {
      const result = promoCode ? await createCheckoutSession(ccId, tier, promoCode) : await createCheckoutSession(ccId, tier)
      if (!result?.url) {
        throw new Error('Checkout did not return a URL.')
      }
      window.location.assign(result.url)
    } catch (error) {
      set({ checkoutPending: false, checkoutError: getFriendlyErrorMessage(error) })
      // The backend self-heals when Stripe already holds a subscription (409 "synced"); reload so
      // the tab reflects it instead of offering to subscribe again.
      await get().loadBilling()
    }
  },

  /** Stops this location's pending downgrade request (closes its support ticket), then reloads the view. */
  cancelDowngrade: async (requestId) => {
    set({ upgradePending: true, upgradeError: null, upgradeMessage: null })
    try {
      await cancelDowngradeRequest(requestId)
      set({ upgradePending: false, upgradeMessage: 'Your downgrade request was cancelled — you stay on your current plan.' })
      await get().loadBilling()
    } catch (error) {
      set({ upgradePending: false, upgradeError: getFriendlyErrorMessage(error) })
    }
  },

  /** Ends a (paused) Stripe trial now: charges the card on file and unlocks the location. */
  endTrial: async () => {
    const ccId = getActiveCcid()
    set({ upgradePending: true, upgradeError: null, upgradeMessage: null })
    try {
      const result = await endTrialNow(ccId)
      set({ upgradePending: false, upgradeMessage: result?.message || 'Your subscription is active.' })
      await get().loadBilling()
      // The nav, routes and status bar gate on entitlements: refresh them so everything unlocks now.
      await useEntitlementStore.getState().loadEntitlements(ccId, { force: true })
    } catch (error) {
      set({ upgradePending: false, upgradeError: getFriendlyErrorMessage(error) })
    }
  },

  /** Prorated, immediate upgrade of the current Stripe subscription, then reloads the view. */
  upgrade: async (tier) => {
    const ccId = getActiveCcid()
    set({ upgradePending: true, upgradeError: null, upgradeMessage: null })
    try {
      const result = await upgradeSubscription(ccId, tier)
      set({ upgradePending: false, upgradeMessage: result?.message || 'Your upgrade has been applied.' })
      await get().loadBilling()
      // The bigger plan unlocks features: refresh the nav, routes and status bar now, not on the next page load.
      await useEntitlementStore.getState().loadEntitlements(ccId, { force: true })
    } catch (error) {
      set({ upgradePending: false, upgradeError: getFriendlyErrorMessage(error) })
    }
  },

  /** Sends the browser to the Stripe Customer Portal for the active center. */
  openPortal: async () => {
    const ccId = getActiveCcid()
    set({ portalPending: true, portalError: null })
    try {
      const result = await createPortalSession(ccId)
      if (!result?.url) {
        throw new Error('The billing portal did not return a URL.')
      }
      window.location.assign(result.url)
    } catch (error) {
      set({ portalPending: false, portalError: getFriendlyErrorMessage(error) })
    }
  }
}))

export { useBillingStore }
export default useBillingStore
